import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  CircleDot,
  FilePen,
  Loader2,
  Play,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api, BACKEND_URL, formatApiError } from "@/lib/api";
import AICostMeter from "@/components/AICostMeter";

const TERMINAL_LABELS = {
  complete: { label: "Generation complete", tone: "success" },
  max_steps: { label: "Stopped — step cap reached", tone: "warning" },
  max_cost: { label: "Stopped — cost cap reached", tone: "warning" },
  timeout: { label: "Stopped — time cap reached", tone: "warning" },
  cancelled: { label: "Cancelled", tone: "warning" },
  auth_failed: { label: "Authentication failed", tone: "error" },
  target_unreachable: { label: "Target URL unreachable", tone: "error" },
  invalid_response: { label: "Model returned invalid JSON", tone: "error" },
  agent_stuck_no_op: { label: "Agent stuck in a no-op loop", tone: "warning" },
  llm_failed: { label: "LLM call failed", tone: "error" },
  playwright_crash: { label: "Browser crashed", tone: "error" },
};

function fmtElapsed(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/**
 * R3 Phase 6A.5 — Live AI generation progress card.
 *
 * Owns the EventSource subscription, renders per-iteration progress
 * (iteration counter, cost meter, elapsed time, last step intent,
 * DOM size), and exposes terminal-state UI when the stream ends.
 *
 * Props:
 *   workflowId, generationId, streamUrl, cancelUrl  — wire data
 *   caps: { max_steps, max_cost_usd, max_duration_seconds }
 *   onTerminated()  — fired AFTER stream_end with the final state so
 *                     the parent can refresh queries / clear storage.
 *
 * Why is the EventSource constructed here and not in the parent?
 * The card needs to reconnect on remount (resume-from-localStorage),
 * and centralising the connection lets us tear it down cleanly when
 * the user navigates away.
 */
export default function AIGenerationProgressCard({
  workflowId,
  generationId,
  streamUrl,
  cancelUrl,
  caps,
  autoRunOnSuccess = false,
  onTerminalReached,
  onDismiss,
  testid = "ai-generation-progress-card",
}) {
  const queryClient = useQueryClient();
  const [frames, setFrames] = useState([]); // for debug; we only render the latest
  const [lastFrame, setLastFrame] = useState(null);
  const [connected, setConnected] = useState(false);
  const [terminal, setTerminal] = useState(null); // null while running
  const [terminalSummary, setTerminalSummary] = useState(null); // backend's authoritative final-state info
  const [cancelling, setCancelling] = useState(false);
  const [sseError, setSseError] = useState(null);
  // R3 Phase 6A.6 — auto-pipeline trigger state.  Lifecycle:
  //   idle → 'triggering' (POST /autorun in flight) → 'started' (run created)
  //                                                  or 'skipped' (ratio < 0.5)
  //                                                  or 'failed' (409/422/etc.)
  // The user can ALSO click the "Auto-run now" CTA in the TerminalCard
  // — that flows through the same handler with triggered_by='user'.
  const [autorunState, setAutorunState] = useState({
    status: "idle",
    triggered_by: null,
    run_id: null,
    run_number: null,
    error: null,
  });
  const autorunFiredRef = useRef(false); // de-dupe the auto path
  const cancelledLocally = useRef(false);
  const esRef = useRef(null);

  // Build the absolute SSE URL.  In the preview proxy the URL is
  // cross-origin, so EventSource must travel with credentials so the
  // cookie session reaches the backend.
  const absoluteStreamUrl = useMemo(() => {
    if (!streamUrl) return "";
    if (streamUrl.startsWith("http")) return streamUrl;
    return `${BACKEND_URL}${streamUrl}`;
  }, [streamUrl]);

  useEffect(() => {
    if (!absoluteStreamUrl) return undefined;
    let alive = true;
    const es = new EventSource(absoluteStreamUrl, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => {
      if (alive) setConnected(true);
    };
    es.onmessage = (ev) => {
      if (!alive) return;
      let payload = null;
      try {
        payload = JSON.parse(ev.data);
      } catch {
        return;
      }
      // R3 Phase 6A.5 — backend now emits a `terminal_summary` frame
      // with terminated_reason + totals BEFORE the stream_end sentinel.
      // Stash it so the TerminalCard can label the result correctly.
      if (payload && payload.event === "terminal_summary") {
        setTerminalSummary(payload);
        return;
      }
      // The stream sends a synthetic "stream_end" event after the
      // generation finishes — that's our terminal signal.
      if (payload && payload.event === "stream_end") {
        setTerminal({ event: "stream_end" });
        es.close();
        return;
      }
      if (payload && payload.event === "fatal_error") {
        setTerminal({ event: "fatal_error", error: payload.error });
        es.close();
        return;
      }
      setLastFrame(payload);
      setFrames((prev) => (prev.length > 100 ? prev.slice(-100) : prev).concat([payload]));
    };
    es.onerror = () => {
      if (!alive) return;
      // EventSource errors are noisy (heartbeat reconnects show up
      // here too).  We only treat them as fatal if the connection is
      // *closed* — Brave / some proxies fire transient errors that
      // recover.
      if (es.readyState === EventSource.CLOSED) {
        setSseError("Stream connection closed before completion.");
        setTerminal((t) => t || { event: "sse_closed" });
      }
    };
    return () => {
      alive = false;
      try {
        es.close();
      } catch {}
      esRef.current = null;
    };
  }, [absoluteStreamUrl]);

  // After terminal state hits, ask the parent to refresh background
  // queries + wipe localStorage.  The card itself STAYS MOUNTED so
  // the user can read the summary and click into the new version.
  // Dismissal is a separate user action (the X button on the
  // TerminalCard wires to onDismiss).
  useEffect(() => {
    if (!terminal) return;
    queryClient.invalidateQueries({ queryKey: ["workflow", workflowId] });
    queryClient.invalidateQueries({ queryKey: ["versions", workflowId] });
    queryClient.invalidateQueries({ queryKey: ["workflow-cost-rollup", workflowId] });
    queryClient.invalidateQueries({ queryKey: ["ai-in-flight"] });
    if (onTerminalReached) onTerminalReached();
  }, [terminal, workflowId, queryClient, onTerminalReached]);

  // R3 Phase 6A.6 — trigger the autorun endpoint.  Used by both the
  // auto-fire effect (triggered_by='auto') AND the user-clicked CTA
  // in the TerminalCard (triggered_by='user').
  const triggerAutorun = useCallback(
    async (triggeredBy) => {
      setAutorunState({
        status: "triggering",
        triggered_by: triggeredBy,
        run_id: null,
        run_number: null,
        error: null,
      });
      try {
        const { data } = await api.post(
          `/workflows/${workflowId}/ai-generate-steps/${generationId}/autorun`,
          { triggered_by: triggeredBy },
        );
        setAutorunState({
          status: "started",
          triggered_by: triggeredBy,
          run_id: data.run_id,
          run_number: data.run_number ?? null,
          error: null,
        });
        toast.success(
          triggeredBy === "auto"
            ? "Auto-run started — narration + voiceover + stitch will follow."
            : "Run started — narration + voiceover + stitch will follow.",
        );
        queryClient.invalidateQueries({ queryKey: ["demo-runs"] });
      } catch (err) {
        const status = err?.response?.status;
        // 422 means the success ratio fell below the threshold AFTER
        // the user opted in — show that explicitly.
        if (status === 422) {
          setAutorunState({
            status: "skipped",
            triggered_by: triggeredBy,
            run_id: null,
            run_number: null,
            error:
              err?.response?.data?.detail?.message ||
              "Success ratio below auto-run threshold.",
          });
          toast.message(
            "Auto-run skipped — success rate too low. Review the generated steps before running manually.",
          );
        } else if (status === 409) {
          setAutorunState({
            status: "failed",
            triggered_by: triggeredBy,
            run_id: err?.response?.data?.detail?.run_id || null,
            run_number: null,
            error: "Another run is already in flight on this workflow.",
          });
          toast.error(
            "Another run is already in flight on this workflow — can't start a second one.",
          );
        } else {
          setAutorunState({
            status: "failed",
            triggered_by: triggeredBy,
            run_id: null,
            run_number: null,
            error: formatApiError(err),
          });
          toast.error(formatApiError(err));
        }
      }
    },
    [workflowId, generationId, queryClient],
  );

  // Auto-fire the autorun chain when the user opted in AND the
  // generation finished above the threshold.  Runs at MOST once per
  // mount — see `autorunFiredRef`.
  useEffect(() => {
    if (!terminalSummary) return;
    if (!autoRunOnSuccess) return;
    if (autorunFiredRef.current) return;
    if (terminalSummary.terminated_reason !== "complete") return;
    const steps = Number(terminalSummary.steps_count) || 0;
    const successes = Number(terminalSummary.success_count) || 0;
    const ratio = steps > 0 ? successes / steps : 0;
    if (ratio < 0.5) {
      // Below threshold — record the skip locally so the UI can
      // explain why nothing fired.  The backend won't be called.
      autorunFiredRef.current = true;
      setAutorunState({
        status: "skipped",
        triggered_by: "auto",
        run_id: null,
        run_number: null,
        error: `Success ratio ${ratio.toFixed(
          2,
        )} below the 0.50 auto-run threshold.`,
      });
      return;
    }
    autorunFiredRef.current = true;
    triggerAutorun("auto");
  }, [terminalSummary, autoRunOnSuccess, triggerAutorun]);

  async function handleCancel() {
    if (!cancelUrl || cancelling || terminal) return;
    setCancelling(true);
    cancelledLocally.current = true;
    try {
      // cancelUrl from the backend already starts with /api/...
      const path = cancelUrl.startsWith("/api/") ? cancelUrl.slice(4) : cancelUrl;
      await api.post(path);
      toast.success("Cancel requested — finishing current step…");
    } catch (err) {
      // 404 means the generation already finished — treat as soft success
      const status = err?.response?.status;
      if (status === 404) {
        toast.message("Generation already finished — nothing to cancel.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setCancelling(false);
    }
  }

  // Derive the running snapshot from the latest frame.
  const iteration = lastFrame?.iteration_n ?? lastFrame?.step_n ?? 0;
  const costSoFar = lastFrame?.cost_so_far_usd ?? 0;
  const elapsed = lastFrame?.time_so_far_s ?? lastFrame?.time_so_far ?? 0;
  const lastIntent =
    lastFrame?.last_step_intent || lastFrame?.intent || "Initializing browser…";
  const domSizeKb = lastFrame?.dom_size_kb;
  const noOpStreak = lastFrame?.no_op_streak ?? 0;

  if (terminal) {
    return (
      <TerminalCard
        workflowId={workflowId}
        generationId={generationId}
        terminal={terminal}
        terminalSummary={terminalSummary}
        lastFrame={lastFrame}
        caps={caps}
        sseError={sseError}
        cancelledLocally={cancelledLocally.current}
        autorunState={autorunState}
        onTriggerAutorun={() => triggerAutorun("user")}
        onDismiss={onDismiss}
        testid={testid}
      />
    );
  }

  return (
    <section
      className="rounded-xl border border-rose-200 bg-rose-50/40 p-5 shadow-sm"
      data-testid={testid}
      data-generation-id={generationId}
      data-state="running"
    >
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <Loader2 size={16} className="animate-spin text-rose-600" />
        <h3 className="text-sm font-semibold tracking-tight text-slate-900">
          AI is generating steps…
        </h3>
        <span
          className="ml-1 inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 font-mono text-[10px] text-slate-500 ring-1 ring-slate-200"
          data-testid={`${testid}-generation-id`}
        >
          <CircleDot size={9} className="text-rose-500" />
          {generationId.slice(0, 8)}
        </span>
        <span
          className="ml-auto text-[11px] text-slate-500"
          data-testid={`${testid}-connection`}
        >
          {connected ? "live" : "connecting…"}
        </span>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Metric
          label="Step"
          value={
            <span data-testid={`${testid}-iteration`}>
              {iteration > 0 ? iteration : "—"}
              <span className="ml-1 text-xs text-slate-400">/ {caps.max_steps}</span>
            </span>
          }
        />
        <Metric
          label="Elapsed"
          value={
            <span data-testid={`${testid}-elapsed`}>
              {fmtElapsed(elapsed)}
              <span className="ml-1 text-xs text-slate-400">
                / {fmtElapsed(caps.max_duration_seconds)}
              </span>
            </span>
          }
        />
        <Metric
          label="DOM"
          value={
            <span data-testid={`${testid}-dom`}>
              {domSizeKb != null ? `${Number(domSizeKb).toFixed(1)} KB` : "—"}
            </span>
          }
        />
      </div>

      <div className="mt-4">
        <AICostMeter
          currentUsd={costSoFar}
          maxUsd={caps.max_cost_usd}
          testid={`${testid}-cost-meter`}
        />
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-400">
          Last action
        </div>
        <div
          className="mt-0.5 text-sm font-medium text-slate-800"
          data-testid={`${testid}-last-intent`}
        >
          {lastIntent}
        </div>
        {noOpStreak >= 2 && (
          <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700">
            <AlertTriangle size={12} />
            Agent has stalled on the same page {noOpStreak}× — will bail at 3 in a row.
          </p>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-[11px] text-slate-500">
          AI-generated steps may need manual review. First-generation success rate
          is typically 75-85%.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          disabled={cancelling || !cancelUrl}
          className="gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
          data-testid={`${testid}-cancel-button`}
        >
          <Ban size={13} />
          {cancelling ? "Cancelling…" : "Cancel"}
        </Button>
      </div>
    </section>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className="mt-0.5 text-base font-semibold tracking-tight text-slate-900">
        {value}
      </div>
    </div>
  );
}

function TerminalCard({
  workflowId,
  generationId,
  terminal,
  terminalSummary,
  lastFrame,
  caps,
  sseError,
  cancelledLocally,
  autorunState,
  onTriggerAutorun,
  onDismiss,
  testid,
}) {
  // Prefer the backend's authoritative summary frame for everything —
  // it's emitted right before stream_end and carries the canonical
  // terminated_reason, totals, and new_version.  Fall back to the
  // last live frame + local cancel flag when summary is missing
  // (older backend, fatal_error path, or proxy dropped the frame).
  const cost =
    terminalSummary?.total_cost_usd ??
    lastFrame?.cost_so_far_usd ??
    0;
  const elapsed =
    terminalSummary?.duration_seconds ??
    lastFrame?.time_so_far_s ??
    lastFrame?.time_so_far ??
    0;
  const stepN =
    terminalSummary?.steps_count ??
    lastFrame?.iteration_n ??
    lastFrame?.step_n ??
    0;
  const successN = Number(terminalSummary?.success_count) || 0;
  const newVersion = terminalSummary?.new_version ?? null;

  // R3 Phase 6A.6 — success-ratio gate for the Auto-run CTA.  The same
  // threshold is enforced server-side in `/autorun`; we mirror it here
  // so the UI shows the correct branch without an extra round-trip.
  const successRatio = stepN > 0 ? successN / stepN : 0;
  const ratioMet = successRatio >= 0.5;

  // Pick the inferred reason.  Backend authoritative wins; otherwise
  // fall back to local cancel flag → fatal_error event → generic.
  let inferred = terminalSummary?.terminated_reason;
  if (!inferred) {
    if (cancelledLocally) inferred = "cancelled";
    else if (terminal?.event === "fatal_error") inferred = "playwright_crash";
    else inferred = "complete"; // optimistic — stream_end without summary
  }
  const meta = TERMINAL_LABELS[inferred] || {
    label: "Generation finished",
    tone: "success",
  };
  const Icon =
    meta.tone === "success"
      ? CheckCircle2
      : meta.tone === "error"
      ? XCircle
      : AlertTriangle;
  const accent =
    meta.tone === "success"
      ? "border-emerald-200 bg-emerald-50/60 text-emerald-900"
      : meta.tone === "error"
      ? "border-rose-200 bg-rose-50/60 text-rose-900"
      : "border-amber-200 bg-amber-50/60 text-amber-900";

  // Auto-run CTA shows whenever the generation produced runnable steps
  // AND the success ratio meets the 50% threshold.  This deliberately
  // does NOT gate on terminated_reason — a user who cancelled at 9/10
  // can still click Auto-run on the partial, and a max_steps/max_cost
  // bail-out with mostly-passing steps is fair game too.  The dismiss
  // X is there for users who explicitly don't want to run anything.
  // Below the ratio we steer them to the editor to review/repair.
  const showAutorunCta = stepN > 0 && ratioMet;
  const showReviewLink = newVersion != null && !ratioMet;

  // Convenience handles for the autorun UI states.
  const ar = autorunState || { status: "idle" };
  const autorunTriggering = ar.status === "triggering";
  const autorunStarted = ar.status === "started";
  const autorunSkipped = ar.status === "skipped";
  const autorunFailed = ar.status === "failed";

  return (
    <section
      className={`rounded-xl border p-5 shadow-sm ${accent}`}
      data-testid={testid}
      data-generation-id={generationId}
      data-state="terminated"
      data-terminated-reason={inferred}
      data-success-ratio={successRatio.toFixed(2)}
      data-autorun-state={ar.status}
    >
      <header className="mb-3 flex items-center gap-2">
        <Icon size={18} />
        <h3 className="text-sm font-semibold tracking-tight">{meta.label}</h3>
        <span className="ml-auto font-mono text-[10px] opacity-60">
          {generationId.slice(0, 8)}
        </span>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-md p-1 opacity-60 transition-opacity hover:bg-white hover:opacity-100"
            data-testid={`${testid}-dismiss-button`}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        )}
      </header>

      <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-4">
        <Pair label="Steps generated" value={stepN || 0} testid={`${testid}-final-steps`} />
        <Pair
          label="Success ratio"
          value={
            stepN > 0
              ? `${successN}/${stepN} (${(successRatio * 100).toFixed(0)}%)`
              : "—"
          }
          testid={`${testid}-final-success-ratio`}
        />
        <Pair
          label="Total cost"
          value={`$${Number(cost).toFixed(4)}`}
          testid={`${testid}-final-cost`}
        />
        <Pair
          label="Duration"
          value={fmtElapsed(elapsed)}
          testid={`${testid}-final-duration`}
        />
      </dl>

      {sseError && !terminalSummary && (
        <p className="mt-3 rounded-md bg-white/70 px-2.5 py-1.5 text-[11px] text-slate-600">
          {sseError}
        </p>
      )}
      {terminalSummary?.error_message && (
        <p
          className="mt-3 rounded-md bg-white/70 px-2.5 py-1.5 text-[11px] text-slate-700"
          data-testid={`${testid}-error-message`}
        >
          {terminalSummary.error_message}
        </p>
      )}

      {/* R3 Phase 6A.6 — autorun status panel (only renders once the
          chain is in motion or has reported a terminal autorun state). */}
      {autorunTriggering && (
        <div
          className="mt-3 flex items-center gap-2 rounded-md bg-white/80 px-3 py-2 text-[12px] text-slate-700"
          data-testid={`${testid}-autorun-status`}
          data-autorun-status="triggering"
        >
          <Loader2 size={13} className="animate-spin" />
          Starting auto-run pipeline…
        </div>
      )}
      {autorunStarted && (
        <div
          className="mt-3 flex flex-wrap items-center gap-3 rounded-md bg-white/80 px-3 py-2 text-[12px] text-slate-700"
          data-testid={`${testid}-autorun-status`}
          data-autorun-status="started"
        >
          <CheckCircle2 size={13} className="text-emerald-600" />
          <span>
            {ar.triggered_by === "auto" ? "Auto-run" : "Run"} started
            {ar.run_number ? ` — run #${ar.run_number}` : ""}
            {" "}
            <span className="text-slate-500">(narration + voiceover + stitch will follow)</span>
          </span>
          {ar.run_id && (
            <Link
              to={`/runs/${ar.run_id}`}
              onClick={onDismiss}
              className="ml-auto inline-flex items-center gap-1 rounded-md bg-slate-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-slate-800"
              data-testid={`${testid}-autorun-view-run-link`}
            >
              <Play size={11} />
              View run
            </Link>
          )}
        </div>
      )}
      {autorunSkipped && (
        <div
          className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900"
          data-testid={`${testid}-autorun-status`}
          data-autorun-status="skipped"
        >
          <span className="font-medium">Auto-run skipped.</span> {ar.error}
        </div>
      )}
      {autorunFailed && (
        <div
          className="mt-3 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-900"
          data-testid={`${testid}-autorun-status`}
          data-autorun-status="failed"
        >
          <span className="font-medium">Auto-run failed.</span> {ar.error}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Link
          to={`/workflows/${workflowId}`}
          onClick={onDismiss}
          className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50"
          data-testid={`${testid}-view-version-link`}
        >
          <Sparkles size={14} className="text-indigo-600" />
          {newVersion
            ? `View generated workflow (v${newVersion})`
            : "View workflow"}
        </Link>

        {showAutorunCta && !autorunStarted && !autorunTriggering ? (
          <Button
            size="sm"
            className="gap-1.5 bg-rose-600 hover:bg-rose-700"
            data-testid={`${testid}-autorun-button`}
            onClick={onTriggerAutorun}
            disabled={!onTriggerAutorun}
          >
            <Play size={14} />
            Auto-run now
          </Button>
        ) : !showAutorunCta && newVersion ? (
          <Link
            to={`/workflows/${workflowId}/edit`}
            onClick={onDismiss}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-50"
            data-testid={`${testid}-review-steps-link`}
          >
            <FilePen size={14} />
            Review generated steps before running
          </Link>
        ) : null}
      </div>
    </section>
  );
}

function Pair({ label, value, testid }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider opacity-70">{label}</dt>
      <dd className="font-mono text-base font-semibold" data-testid={testid}>
        {value}
      </dd>
    </div>
  );
}
